import React, { forwardRef } from "react";
import { Message } from "@/types";
import sparkleIcon from "@/assets/sparkle.svg";
import WorkingIndicator from "./WorkingIndicator";
import ChatMessage from "./ChatMessage";

interface ChatMessageListProps {
  messages: Message[];
  isLoadingConversation: boolean;
  /**
   * Whether to render a working indicator at the bottom (only when an
   * execution is active and there is no streaming placeholder yet).
   */
  showWorkingIndicator: boolean;
}

/**
 * Scrollable list of chat messages.
 *
 * The forwarded ref points to the scroll container so the parent can wire
 * it up to the auto-scroll hook.
 */
export const ChatMessageList = forwardRef<HTMLDivElement, ChatMessageListProps>(
  function ChatMessageList(
    { messages, isLoadingConversation, showWorkingIndicator },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className="flex-1 overflow-y-auto"
        style={{ overflowAnchor: "none" }}
        tabIndex={-1}
      >
        <div className="p-4 space-y-4">
          {isLoadingConversation && messages.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">
                Loading conversation history...
              </div>
            </div>
          )}

          {messages.length > 0 &&
            messages.map((message) => (
              <div key={message.id} className="space-y-2">
                <ChatMessage message={message} />
              </div>
            ))}

          {showWorkingIndicator && (
            <div className="flex items-start gap-3">
              <img
                src={sparkleIcon}
                alt="AI"
                className="w-5 h-5 mt-1 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <WorkingIndicator showThinkingHint={false} />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default ChatMessageList;
